import * as m from '@staiter/i18n';
import type { Locale, UserPreferences } from '@staiter/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { PageHeader } from '~/components/data';
import { useToast } from '~/components/feedback';
import { Button, Input, Select } from '~/components/primitives';
import {
  changePassword,
  deleteAvatar,
  disableTotp,
  fetchMe,
  fetchTotpStatus,
  regenerateRecoveryCodes,
  requestEmailChange,
  setupTotp,
  updateProfile,
  uploadAvatar,
  verifyTotp,
} from '~/lib/api/auth';
import { fetchMyPreferences, updateMyPreferences } from '~/lib/api/me';
import { formatDateTime } from '~/lib/format';
import { useLocale } from '~/lib/hooks/useLocale';
import { useUpdateLocale } from '~/lib/hooks/useUpdateLocale';
import { useAuthStore } from '~/store/auth';
import shared from './_auth._shared.module.css';
import styles from './_auth.admin.module.css';
import prefStyles from './_auth.me.preferences.module.css';

export const Route = createFileRoute('/_auth/me/preferences')({ component: PreferencesPage });

function PreferencesPage(): JSX.Element {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['me', 'preferences'], queryFn: fetchMyPreferences });
  const [local, setLocal] = useState<UserPreferences | null>(null);
  const businessTz = useAuthStore((s) => s.user?.business_timezone) || 'Asia/Almaty';

  useEffect(() => {
    if (data) setLocal(data as UserPreferences);
  }, [data]);

  const mut = useMutation({
    mutationFn: (patch: Partial<UserPreferences>) => updateMyPreferences(patch),
    onSuccess: (next) => {
      qc.setQueryData(['me', 'preferences'], next);
      setLocal(next as UserPreferences);
    },
  });

  const commit = (patch: Partial<UserPreferences>): void => {
    if (!local) return;
    const next = { ...local, ...patch };
    setLocal(next);
    mut.mutate(patch);
  };

  if (!local) {
    return (
      <div className={shared.page}>
        <PageHeader title={m.preferences_page_title()} />
        <p className={styles.muted}>{m.common_loader_loading()}</p>
      </div>
    );
  }

  return (
    <div className={shared.page}>
      <PageHeader title={m.page_title_preferences()} description="" />
      <AvatarSection />
      <ProfileSection />
      <EmailSection />
      <LanguageSection />
      <div className={shared.formGridAuto}>
        <Select
          label={m.preferences_theme_label()}
          value={local.theme}
          onChange={(e) => commit({ theme: e.target.value as UserPreferences['theme'] })}
          options={[
            { value: 'light', label: m.preferences_theme_light() },
            { value: 'dark', label: m.preferences_theme_dark() },
            { value: 'system', label: m.preferences_theme_system() },
          ]}
        />
        <Select
          label={m.preferences_sidebar_label()}
          value={local.sidebar_collapsed ? 'true' : 'false'}
          onChange={(e) => commit({ sidebar_collapsed: e.target.value === 'true' })}
          options={[
            { value: 'false', label: m.preferences_sidebar_expanded() },
            { value: 'true', label: m.preferences_sidebar_collapsed() },
          ]}
        />
        <Select
          label={m.preferences_timezone_label()}
          // F94 (§9.5, К3-29): пояс профиля — для отображения времени; сроки задач считает
          // деловой пояс компании (`/auth/me`), и это надо сказать прямо у поля.
          hint={
            businessTz === 'Asia/Almaty'
              ? m.preferences_timezone_business_almaty()
              : m.preferences_timezone_business_other({ zone: businessTz })
          }
          value={local.timezone}
          onChange={(e) => commit({ timezone: e.target.value })}
          options={[
            { value: 'Asia/Almaty', label: 'Asia/Almaty (UTC+5)' },
            { value: 'Asia/Astana', label: 'Asia/Astana (UTC+5)' },
            { value: 'Europe/Moscow', label: 'Europe/Moscow (UTC+3)' },
            { value: 'UTC', label: 'UTC' },
          ]}
        />
        <Select
          label={m.preferences_density_label()}
          value={local.density}
          onChange={(e) => commit({ density: e.target.value as UserPreferences['density'] })}
          options={[
            { value: 'comfortable', label: 'Comfortable' },
            { value: 'compact', label: 'Compact' },
          ]}
        />
        <Select
          label={m.preferences_run_task_label()}
          value={local.default_run_task_type ?? ''}
          onChange={(e) =>
            commit({
              default_run_task_type: (e.target.value ||
                null) as UserPreferences['default_run_task_type'],
            })
          }
          options={[
            { value: '', label: m.preferences_run_task_none() },
            { value: 'inventory_reconcile', label: m.preferences_run_task_reconcile() },
            { value: 'writeoff_review', label: m.preferences_run_task_writeoff() },
            { value: 'cost_draft', label: m.preferences_run_task_cost() },
            { value: 'anomaly_investigation', label: m.preferences_run_task_anomaly() },
            { value: 'period_close_draft', label: m.preferences_run_task_period_close() },
          ]}
        />
      </div>
      <p className={styles.muted} aria-live="polite">
        {mut.isPending ? m.preferences_saving() : m.preferences_auto_save_hint()}
      </p>
      <NotificationsLink />
      <ChangePasswordSection />
      <TwoFactorSection />
    </div>
  );
}

/**
 * F12 (notification-sla-lifecycle §9.2) — секция «Уведомления» удалена.
 *
 * В ней жили четыре контрола (email approvals/conflicts/anomalies + «частота
 * дайджеста»), которых НЕ ЧИТАЕТ ни один отправитель: `me.ts:42-45` их только
 * хранит, honor-логики не существует. Пользователь крутил тумблеры без эффекта —
 * переносить такую фикцию в новую поверхность значило бы узаконить обман.
 * Поля в `user_preferences` и API не тронуты (совместимость); honor-логика
 * agent-контура — зафиксированный долг (SOLUTION §8 п.12), не этот пак.
 *
 * Вместо секции — ссылка на единственную живую поверхность настроек уведомлений.
 */
function NotificationsLink(): JSX.Element {
  return (
    <section style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid var(--color-border)' }}>
      <h2 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 12px 0' }}>
        {m.tm_notifications()}
      </h2>
      <p style={{ margin: 0 }}>
        <Link to="/notifications" search={{ tab: 'settings' }} style={{ color: 'var(--accent)' }}>
          {m.tm_notif_prefs_link_center()}
        </Link>
      </p>
    </section>
  );
}

/**
 * Профиль — аватар пользователя. Загрузка (PNG/JPEG/WebP до 5 МБ) через
 * getStorage, presigned URL. После смены обновляем auth-store, чтобы TopBar
 * подхватил новую картинку.
 */
function AvatarSection(): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: fetchMe });
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const avatarUrl = (me as { avatar_url?: string | null } | undefined)?.avatar_url ?? null;
  const fullName = (me as { full_name?: string | null } | undefined)?.full_name ?? '';
  const initial = fullName.trim()[0]?.toUpperCase() ?? 'U';

  const refreshStore = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: ['me'] });
    try {
      const fresh = await fetchMe();
      useAuthStore.getState().setMe(fresh);
    } catch {
      // store остаётся прежним — не критично
    }
  };

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      await uploadAvatar(file);
      await refreshStore();
      toast.push({ variant: 'success', title: m.prefs_avatar_updated() });
    } catch (err) {
      toast.push({
        variant: 'error',
        title: m.prefs_avatar_upload_failed(),
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const onRemove = async (): Promise<void> => {
    setBusy(true);
    try {
      await deleteAvatar();
      await refreshStore();
      toast.push({ variant: 'success', title: m.prefs_avatar_removed() });
    } catch (err) {
      toast.push({
        variant: 'error',
        title: m.job_card_toast_delete_failed(),
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      style={{ marginBottom: 24, paddingBottom: 24, borderBottom: '1px solid var(--color-border)' }}
    >
      <h2 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 16px 0' }}>{m.ui_avatar()}</h2>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={m.ui_avatar()}
            width={64}
            height={64}
            style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover' }}
          />
        ) : (
          <span
            aria-hidden="true"
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 24,
              fontWeight: 600,
              color: 'var(--color-text-on-accent, #fff)',
              background: 'var(--color-accent, #4b5563)',
            }}
          >
            {initial}
          </span>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={onPick}
            style={{ display: 'none' }}
          />
          <Button
            variant="secondary"
            size="sm"
            disabled={busy}
            onClick={() => fileRef.current?.click()}
          >
            {busy ? m.app_title_loading() : m.prefs_avatar_upload()}
          </Button>
          {avatarUrl && (
            <Button variant="ghost" size="sm" disabled={busy} onClick={onRemove}>
              {m.ui_udalit()}
            </Button>
          )}
        </div>
      </div>
      <p className={styles.muted} style={{ marginTop: 12, marginBottom: 0 }}>
        {m.ui_png_jpeg_ili_webp_do_5_mb()}
      </p>
    </section>
  );
}

/**
 * Профиль — смена email (логин). Двухшаговая: запрос → письмо-подтверждение на
 * новый адрес → смена применяется по ссылке из письма.
 */
function EmailSection(): JSX.Element {
  const toast = useToast();
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: fetchMe });
  const currentEmail = (me as { email?: string } | undefined)?.email ?? '';
  const [newEmail, setNewEmail] = useState('');
  const [password, setPassword] = useState('');
  const [sent, setSent] = useState(false);

  const mut = useMutation({
    mutationFn: () =>
      requestEmailChange({ new_email: newEmail.trim(), current_password: password }),
    onSuccess: () => {
      setSent(true);
      setNewEmail('');
      setPassword('');
      toast.push({
        variant: 'success',
        title: m.prefs_email_sent(),
        description: m.prefs_email_check_new(),
      });
    },
    onError: (err: Error) =>
      toast.push({ variant: 'error', title: m.prefs_failed(), description: err.message }),
  });

  const emailValid = /.+@.+\..+/.test(newEmail.trim());
  const canSubmit = emailValid && password.length > 0 && !mut.isPending;

  return (
    <section
      style={{ marginBottom: 24, paddingBottom: 24, borderBottom: '1px solid var(--color-border)' }}
    >
      <h2 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 16px 0' }}>
        {m.ui_email_dlya_vhoda()}
      </h2>
      <p className={styles.muted} style={{ marginTop: 0, marginBottom: 16 }}>
        {m.ui_tekuschiy_adres()} <strong>{currentEmail}</strong>
        {m.me_email_change_note()}
      </p>
      {sent ? (
        <p className={styles.muted} style={{ margin: 0 }} aria-live="polite">
          {m.ui_pismo_otpravleno_otkroyte_ssylku_v_pisme_na_novom_adrese()}
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 420 }}>
          <Input
            label={m.prefs_new_email()}
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            autoComplete="email"
          />
          <Input
            label={m.force_pwd_current()}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
          />
          <Button
            variant="primary"
            disabled={!canSubmit}
            onClick={() => mut.mutate()}
            style={{ alignSelf: 'flex-start' }}
          >
            {mut.isPending ? m.common_loader_submitting() : m.prefs_send_confirmation()}
          </Button>
        </div>
      )}
    </section>
  );
}

/**
 * SET-AUDIT-9 (pass3 settings audit): Self-edit full_name + phone.
 * Fetches /me, allows update via PATCH /me/profile.
 */
function ProfileSection(): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: fetchMe });
  const [fullName, setFullName] = useState<string>('');
  const [phone, setPhone] = useState<string>('');

  useEffect(() => {
    if (me) {
      setFullName((me as { full_name?: string | null }).full_name ?? '');
      setPhone((me as { phone?: string | null }).phone ?? '');
    }
  }, [me]);

  const mut = useMutation({
    mutationFn: () =>
      updateProfile({ full_name: fullName.trim() || null, phone: phone.trim() || null }),
    onSuccess: () => {
      toast.push({ variant: 'success', title: m.prefs_profile_saved() });
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
    onError: (err: Error) =>
      toast.push({
        variant: 'error',
        title: m.route_item_detail_toast_save_failed(),
        description: err.message,
      }),
  });

  const meFullName = (me as { full_name?: string | null } | undefined)?.full_name ?? '';
  const mePhone = (me as { phone?: string | null } | undefined)?.phone ?? '';
  const dirty = fullName !== meFullName || phone !== mePhone;

  return (
    <section
      style={{ marginBottom: 24, paddingBottom: 24, borderBottom: '1px solid var(--color-border)' }}
    >
      <h2 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 16px 0' }}>{m.ui_profil()}</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 420 }}>
        <Input
          label={m.prefs_full_name()}
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
        />
        <Input
          label={m.ui_telefon()}
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
        <Button
          variant="primary"
          disabled={!dirty || mut.isPending}
          onClick={() => mut.mutate()}
          style={{ alignSelf: 'flex-start' }}
        >
          {mut.isPending ? m.common_loader_saving() : m.common_button_save()}
        </Button>
      </div>
    </section>
  );
}

/**
 * SET-AUDIT-2 (pass3 settings audit): Self-service change password.
 * Раньше user без mustReset flag не имел способа изменить свой пароль.
 * BE endpoint /auth/password-change уже существовал — добавлен FE form.
 */
function ChangePasswordSection(): JSX.Element {
  const toast = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');

  const mut = useMutation({
    mutationFn: () =>
      changePassword({ current_password: current, new_password: next, confirm_password: confirm }),
    onSuccess: () => {
      toast.push({
        variant: 'success',
        title: m.prefs_password_changed(),
        description: m.prefs_sign_in_again(),
      });
      setCurrent('');
      setNext('');
      setConfirm('');
      // Auto-logout с redirect to login после успешной смены.
      setTimeout(() => {
        window.location.href = '/login';
      }, 1500);
    },
    onError: (err: Error) =>
      toast.push({
        variant: 'error',
        title: m.reset_password_network_title(),
        description: err.message,
      }),
  });

  const canSubmit = current.length > 0 && next.length >= 8 && next === confirm && !mut.isPending;
  const mismatch = confirm.length > 0 && next !== confirm;

  return (
    <section style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid var(--color-border)' }}>
      <h2 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 16px 0' }}>{m.ui_bezopasnost()}</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 420 }}>
        <Input
          label={m.force_pwd_current()}
          type="password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          autoComplete="current-password"
        />
        <Input
          label={m.force_pwd_new()}
          type="password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          hint={m.force_pwd_new_hint()}
          autoComplete="new-password"
        />
        <Input
          label={m.force_pwd_confirm()}
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          {...(mismatch ? { error: m.prefs_passwords_mismatch() } : {})}
          autoComplete="new-password"
        />
        <Button
          variant="primary"
          disabled={!canSubmit}
          onClick={() => mut.mutate()}
          style={{ alignSelf: 'flex-start' }}
        >
          {mut.isPending ? m.common_loader_saving() : m.prefs_change_password()}
        </Button>
      </div>
    </section>
  );
}

/**
 * SET-AUDIT-4 — TOTP 2FA section. Three-state flow:
 *  - not configured → m.me_2fa_enable() → setup secret + QR + verify code → enabled
 *  - enabled → show last-used + recovery codes count → m.me_2fa_disable() / "Reissue codes"
 */
function TwoFactorSection(): JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();
  const { data: status, isLoading } = useQuery({
    queryKey: ['me', '2fa', 'status'],
    queryFn: fetchTotpStatus,
  });

  const [setupData, setSetupData] = useState<{
    secret: string;
    otpauth_uri: string;
    account: string;
    issuer: string;
  } | null>(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [recoveryShown, setRecoveryShown] = useState<string[] | null>(null);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [showDisable, setShowDisable] = useState(false);

  const setupMut = useMutation({
    mutationFn: setupTotp,
    onSuccess: (data) => setSetupData(data),
    onError: (e: Error) =>
      toast.push({
        variant: 'error',
        title: m.prefs_2fa_setup_failed(),
        description: e.message,
      }),
  });

  const verifyMut = useMutation({
    mutationFn: (code: string) => verifyTotp(code),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['me', '2fa', 'status'] });
      if (res.recovery_codes) {
        setRecoveryShown(res.recovery_codes);
        toast.push({
          variant: 'success',
          title: m.prefs_2fa_enabled(),
          description: m.prefs_2fa_save_codes(),
        });
      } else {
        toast.push({ variant: 'success', title: m.prefs_2fa_code_verified() });
      }
      setSetupData(null);
      setVerifyCode('');
    },
    onError: (e: Error) =>
      toast.push({ variant: 'error', title: m.prefs_2fa_code_invalid(), description: e.message }),
  });

  const disableMut = useMutation({
    mutationFn: () => disableTotp({ password: disablePassword, code: disableCode }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me', '2fa', 'status'] });
      toast.push({ variant: 'success', title: m.prefs_2fa_disabled() });
      setShowDisable(false);
      setDisablePassword('');
      setDisableCode('');
      setRecoveryShown(null);
    },
    onError: (e: Error) =>
      toast.push({ variant: 'error', title: m.prefs_2fa_disable_failed(), description: e.message }),
  });

  const regenMut = useMutation({
    mutationFn: (code: string) => regenerateRecoveryCodes(code),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['me', '2fa', 'status'] });
      setRecoveryShown(res.recovery_codes);
      toast.push({
        variant: 'success',
        title: m.prefs_2fa_codes_regenerated(),
        description: m.prefs_2fa_old_codes_dead(),
      });
    },
    onError: (e: Error) =>
      toast.push({
        variant: 'error',
        title: m.job_card_toast_update_failed(),
        description: e.message,
      }),
  });

  if (isLoading) return <></>;

  const enabled = status?.enabled ?? false;

  return (
    <section style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid var(--color-border)' }}>
      <h2 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 16px 0' }}>
        {m.ui_dvuhfaktornaya_autentifikaciya_totp()}
      </h2>
      <p className={styles.muted} style={{ marginTop: 0, marginBottom: 16, maxWidth: 600 }}>
        {m.me_2fa_note()}
      </p>

      {!enabled && !setupData && (
        <Button variant="primary" onClick={() => setupMut.mutate()} disabled={setupMut.isPending}>
          {setupMut.isPending ? m.app_title_loading() : m.prefs_2fa_enable()}
        </Button>
      )}

      {setupData && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            maxWidth: 480,
            padding: 16,
            border: '1px solid var(--color-border)',
            borderRadius: 8,
            background: 'var(--bg-surface)',
          }}
        >
          <p style={{ margin: 0, fontSize: 14 }}>
            <strong>{m.ui_shag_1()}</strong> {m.me_2fa_scan_note()}
          </p>
          {/* QR через Google Charts API (нативно, без локального генератора). */}
          <img
            src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(setupData.otpauth_uri)}`}
            alt={m.prefs_2fa_qr_alt()}
            width={200}
            height={200}
            style={{ alignSelf: 'flex-start', background: 'white', padding: 8, borderRadius: 4 }}
          />
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {m.ui_akkaunt()} <code>{setupData.account}</code>
            <br />
            Секрет (если QR не работает):{' '}
            <code style={{ wordBreak: 'break-all' }}>{setupData.secret}</code>
          </div>
          <p style={{ margin: 0, fontSize: 14 }}>
            <strong>{m.ui_shag_2()}</strong> {m.ui_vvedite_6_znachnyy_kod_iz_prilozheniya()}
          </p>
          <Input
            label={m.prefs_2fa_code_label()}
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={verifyCode}
            onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
            placeholder="123456"
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              variant="primary"
              onClick={() => verifyMut.mutate(verifyCode)}
              disabled={verifyCode.length !== 6 || verifyMut.isPending}
            >
              {verifyMut.isPending ? m.integ_detail_testing() : m.common_button_confirm()}
            </Button>
            <Button variant="ghost" onClick={() => setSetupData(null)}>
              {m.ui_otmena()}
            </Button>
          </div>
        </div>
      )}

      {recoveryShown && (
        <div
          style={{
            marginTop: 16,
            padding: 16,
            border: '1px solid var(--color-warn, var(--color-border))',
            borderRadius: 8,
            background:
              'color-mix(in oklch, var(--color-warn, var(--bg-surface)), transparent 90%)',
            maxWidth: 480,
          }}
        >
          <strong style={{ display: 'block', marginBottom: 8 }}>
            {m.ui_rezervnye_kody_pokazyvayutsya_odin_raz()}
          </strong>
          <p style={{ margin: '0 0 12px 0', fontSize: 13 }}>{m.me_2fa_backup_note()}</p>
          <div
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 14,
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 6,
            }}
          >
            {recoveryShown.map((c) => (
              <code key={c}>{c}</code>
            ))}
          </div>
          <Button
            variant="ghost"
            size="sm"
            style={{ marginTop: 12 }}
            onClick={() => {
              void navigator.clipboard.writeText(recoveryShown.join('\n'));
              toast.push({ variant: 'info', title: m.prefs_2fa_codes_copied() });
            }}
          >
            {m.ui_skopirovat_vse()}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            style={{ marginTop: 12, marginLeft: 8 }}
            onClick={() => setRecoveryShown(null)}
          >
            {m.ui_ya_sohranil_kody()}
          </Button>
        </div>
      )}

      {enabled && !setupData && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
            ✓ 2FA включена.
            {status?.last_used_at && (
              <> Последнее использование: {formatDateTime(status.last_used_at)}.</>
            )}{' '}
            Резервных кодов осталось: {status?.recovery_codes_remaining ?? 0}.
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button variant="secondary" size="sm" onClick={() => setShowDisable((v) => !v)}>
              {m.ui_otklyuchit_2fa()}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                const code = window.prompt(m.prefs_2fa_prompt_current_code());
                if (code) regenMut.mutate(code);
              }}
              disabled={regenMut.isPending}
            >
              {m.ui_sgenerirovat_novye_rezervnye_kody()}
            </Button>
          </div>
          {showDisable && (
            <div
              style={{
                marginTop: 8,
                padding: 12,
                border: '1px solid var(--color-border)',
                borderRadius: 6,
                background: 'var(--bg-surface)',
                maxWidth: 360,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <Input
                label={m.force_pwd_current()}
                type="password"
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
              />
              <Input
                label={m.prefs_2fa_code_6digit()}
                inputMode="numeric"
                maxLength={6}
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ''))}
              />
              <Button
                variant="primary"
                size="sm"
                onClick={() => disableMut.mutate()}
                disabled={!disablePassword || disableCode.length !== 6 || disableMut.isPending}
              >
                {m.ui_podtverdit_otklyuchenie()}
              </Button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

/** F-A.6 — секция «Язык интерфейса». */
function LanguageSection(): JSX.Element {
  const locale = useLocale();
  const update = useUpdateLocale();

  const options: Array<{ value: Locale; label: string; disabled: boolean }> = [
    { value: 'ru', label: m.locale_name({ code: 'ru' }), disabled: false },
    { value: 'en', label: m.locale_name({ code: 'en' }), disabled: false },
    {
      value: 'kk',
      label: `${m.locale_name({ code: 'kk' })} — ${m.locale_coming_soon()}`,
      disabled: true,
    },
    {
      value: 'zh',
      label: `${m.locale_name({ code: 'zh' })} — ${m.locale_coming_soon()}`,
      disabled: true,
    },
  ];

  return (
    <section className={prefStyles.languageSection}>
      <h2>{m.preferences_section_language()}</h2>
      <Select
        label={m.preferences_language_label()}
        value={locale}
        onChange={(e) => {
          const next = e.target.value as Locale;
          if (!options.find((o) => o.value === next && !o.disabled)) return;
          update.mutate(next);
        }}
        options={options.map((o) => ({
          value: o.value,
          label: o.label,
          disabled: o.disabled,
        }))}
      />
      <p className={prefStyles.hint}>{m.preferences_language_hint()}</p>
    </section>
  );
}
